import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Figma text style = CSS class in `src/styles/headings.css`. */
export type HeadingTag = 'display' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5';

type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div';

export type HeadingProps = {
  tag: HeadingTag;
  /** Semantic element; defaults to `h1` for `display`, otherwise to `tag`. */
  as?: HeadingElement;
  /**
   * HTML from the administration: `<span>` = green, `<u>` = yellow underline,
   * ` <br>` = line break from `md`, `<strong>` = emphasis.
   */
  title: string;
  /** Eyebrow above the title (plain text or HTML). */
  subtitle?: string;
  /** `dark` = on dark or green surfaces, `brand` = CTA banner (lighter green highlight). */
  tone?: 'light' | 'dark' | 'brand';
  align?: 'left' | 'center';
  id?: string;
  className?: {
    root?: string;
    subtitle?: string;
    title?: string;
  };
};

export function Heading({
  tag,
  as,
  title,
  subtitle,
  tone = 'light',
  align = 'left',
  id,
  className,
}: HeadingProps) {
  if (!title) return null;

  const Element = as ?? (tag === 'display' ? 'h1' : tag);
  const titleElement = (
    <Element
      id={id}
      className={cn('ea-heading__title', tag, className?.title)}
      dangerouslySetInnerHTML={{ __html: title }}
    />
  );

  return (
    <div
      className={cn(
        'ea-heading flex flex-col gap-2 md:gap-4',
        tone === 'dark' && 'ea-heading--dark',
        tone === 'brand' && 'ea-heading--brand',
        align === 'center' && 'items-center text-center',
        className?.root,
      )}
    >
      {subtitle ? (
        <p
          className={cn('ea-heading__subtitle', className?.subtitle)}
          dangerouslySetInnerHTML={{ __html: subtitle }}
        />
      ) : null}
      {titleElement}
    </div>
  );
}

/**
 * Only the title element, without the wrapper — for places where a `div` is not allowed (e.g.
 * inside `<summary>`). Light tone only.
 */
export function HeadingTitle({
  tag,
  as,
  title,
  className,
}: Pick<HeadingProps, 'tag' | 'as' | 'title'> & { className?: string }) {
  if (!title) return null;

  const Element = as ?? (tag === 'display' ? 'h1' : tag);
  return (
    <Element
      className={cn('ea-heading__title', tag, className)}
      dangerouslySetInnerHTML={{ __html: title }}
    />
  );
}
